import type { AgentPresenceColorScheme } from '@atlaskit/agent-color/agent-presence-color-types';
import { getAgentPresenceColor } from '@atlaskit/agent-color/get-agent-presence-color';
import { getThirdPartyAgentColor } from '@atlaskit/agent-color/get-third-party-agent-color';
import { getParticipantColor } from '@atlaskit/editor-shared-styles/utils';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { token } from '@atlaskit/tokens';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

/**
 * Which badge treatment the telepointer renders for an agent.
 * First-party agents show an avatar without a name; third-party agents keep their name.
 */
export type TelepointerAgentKind = 'agent' | 'agent-third-party' | 'fallback' | 'rovo';

/** The agent identifiers shared avatar resolution keys on. */
export interface TelepointerAgentIdentity {
	/**
	 * The agent's own id in its source system (e.g. Rovo's fixed agent UUID). Distinct from
	 * `agentIdentityAccountId`: this identifies *which agent*, not an Atlassian identity.
	 */
	agentId?: string;
	/** The Atlassian identity-service account id provisioned for the agent, if any (used to fetch/generate its avatar). Distinct from `agentId`. */
	agentIdentityAccountId?: string | null;
	agentName?: string;
	agentNamedId?: string | null;
	/**
	 * Forge / third-party / remote agent, derived on the producer side from the agent record's
	 * `creator_type`. Third parties retain their name alongside the avatar.
	 */
	isThirdParty?: boolean;
}

/**
 * The colours the telepointer paints for an agent. Badge, cursor line and content highlight
 * take one hue, so a producer sets all three from this single result.
 */
export interface TelepointerPresentation {
	agentColor?: AgentPresenceColorScheme;
	highlightBackgroundColor: string;
	highlightBorderColor: string;
	highlightTextColor?: string;
	/** Shared participant-palette index, retained for the legacy identity-hashed presentation. */
	participantColorIndex?: number;
	telepointerAgentKind: TelepointerAgentKind;
	telepointerLineColor: string;
}

const NEUTRAL_GRAY: Omit<TelepointerPresentation, 'telepointerAgentKind'> = {
	highlightBackgroundColor: token('color.background.accent.gray.subtlest'),
	highlightBorderColor: token('color.border.accent.gray'),
	// A background token, because this paints the badge fill and the cursor line.
	telepointerLineColor: token('color.background.accent.gray.subtler'),
};

export const getRovoTelepointerPresentation = (): TelepointerPresentation => ({
	highlightBackgroundColor: token('color.background.accent.purple.subtlest'),
	highlightBorderColor: token('color.border.accent.purple'),
	telepointerAgentKind: 'rovo',
	telepointerLineColor: token('color.rovo.background.brand.bold'),
});

/**
 * Shares badge, cursor and highlight colours across producers. The redesign uses Studio and
 * recognised brand palettes; its control cohort retains the shared collaborator palette.
 * Unknown third parties and missing identities remain neutral.
 */
export const resolveTelepointerPresentation = (
	identity?: TelepointerAgentIdentity,
): TelepointerPresentation => {
	// Rovo always presents as itself, so this badge is fixed rather than identity driven.
	if (identity && isDefaultRovoChat(identity)) {
		return getRovoTelepointerPresentation();
	}

	const { agentId, agentIdentityAccountId, agentNamedId, agentName, isThirdParty } = identity ?? {};

	// Any one of the three identifiers shared agent-avatar resolution keys on is enough to
	// draw an avatar, which leaves the neutral badge for a complete absence of all of them.
	if (agentNamedId || agentIdentityAccountId || agentId) {
		const redesignEnabled =
			isExperimentEnabled('platform_editor_agent_mentions') &&
			isExperimentEnabled('platform_editor_agent_telepointer_redesign');
		if (isThirdParty) {
			const brand = redesignEnabled
				? getThirdPartyAgentColor({ agentNamedId, agentName })
				: undefined;
			return brand
				? {
						agentColor: brand.scheme,
						highlightBackgroundColor: brand.background,
						highlightBorderColor: brand.border,
						highlightTextColor: brand.text,
						telepointerLineColor: brand.bold,
						telepointerAgentKind: 'agent-third-party',
					}
				: { ...NEUTRAL_GRAY, telepointerAgentKind: 'agent-third-party' };
		}

		if (redesignEnabled) {
			const color = getAgentPresenceColor({
				agentId,
				agentIdentityAccountId,
				agentNamedId: agentNamedId ?? undefined,
			});

			return {
				agentColor: color.scheme,
				highlightBackgroundColor: color.background,
				highlightBorderColor: color.border,
				telepointerLineColor: color.bold,
				telepointerAgentKind: 'agent',
			};
		}

		// Hash the identity to one of the shared participant colours — the same palette (and the
		// same `getParticipantColor` function) that colours human collaboration cursors, and that
		// the show-diff attribution system (version history / suggestions) hashes step attribution
		// onto. This keeps one agent's colour consistent everywhere it is attributed, rather than
		// matching only that agent's own avatar illustration.
		const { color, index } = getParticipantColor(
			agentId || agentNamedId || agentIdentityAccountId || '',
		);

		return {
			highlightBackgroundColor: color.svgBackgroundColor,
			highlightBorderColor: color.backgroundColor,
			participantColorIndex: index,
			telepointerAgentKind: 'agent',
			telepointerLineColor: color.backgroundColor,
		};
	}

	return { ...NEUTRAL_GRAY, telepointerAgentKind: 'fallback' };
};
